import { createAsyncThunk } from '@reduxjs/toolkit';

import { AccountResponseDto, SignUpUserResponseDto, type AsyncThunkConfig, SignUpUserRequestItem } from 'types/types';
import {
  connectFacilityAndAdmin,
  connectFacilityAndCaregiver,
  connectFacilityAndPatient,
} from 'api/controllers/facility-controller';
import { getAccountByEmail, createUser as _createUser } from 'api/controllers/account-controller';
import { createAdmin as _createAdmin } from 'api/controllers/admin-controller';
import { NotificationType } from 'types/enums';
import { notificationActions } from '../notification';

const getUser = createAsyncThunk<AccountResponseDto, string, AsyncThunkConfig>('user/getUser', async (email) => {
  return await getAccountByEmail(email);
});

const createUser = createAsyncThunk<SignUpUserResponseDto, SignUpUserRequestItem, AsyncThunkConfig>(
  'user/createUser',
  async (user) => {
    const { facilities, ...data } = user;

    const userResponse = await _createUser(data);
    if (userResponse.caregiverId && facilities.length) {
      await Promise.allSettled(
        facilities.map((facility) =>
          connectFacilityAndCaregiver({ facilityId: facility, caregiverId: userResponse.caregiverId }),
        ),
      );
    }
    if (userResponse.adminId && facilities.length) {
      await Promise.allSettled(
        facilities.map((facility) => connectFacilityAndAdmin({ facilityId: facility, adminId: userResponse.adminId })),
      );
    }
    if (userResponse.patientId && facilities.length) {
      await Promise.allSettled(
        facilities.map((facility) =>
          connectFacilityAndPatient({ facilityId: facility, patientId: userResponse.patientId }),
        ),
      );
    }

    return userResponse;
  },
);

const createAdmin = createAsyncThunk<void, SignUpUserRequestItem, AsyncThunkConfig>(
  'user/createAdmin',
  async (user, { dispatch }) => {
    const { facilities, ...data } = user;

    const userResponse = await _createAdmin(data);

    if (facilities.length) {
      await Promise.allSettled(
        facilities.map((facility) => connectFacilityAndAdmin({ facilityId: facility, adminId: userResponse.adminId })),
      );
    }

    dispatch(notificationActions.notify({ message: 'Admin is registered', type: NotificationType.SUCCESS }));
  },
);

export { getUser, createAdmin, createUser };
